import React, { useEffect, useState } from 'react';
import {
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Alert,
  Snackbar
} from '@mui/material';
import taskEntryService, { TaskEntry } from '../services/taskEntryService';
import authService from '../services/authService';

const TaskEntryComponent: React.FC = () => {
  const [taskEntries, setTaskEntries] = useState<TaskEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [currentTaskEntry, setCurrentTaskEntry] = useState<TaskEntry | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const currentUser = authService.getCurrentUser();
  const currentUserRole = currentUser?.role || '';
  const currentUserDepartment = currentUser?.department || '';

  useEffect(() => {
    fetchTaskEntries();
  }, []);

  const fetchTaskEntries = async () => {
    try {
      setLoading(true);
      const fetchedEntries = await taskEntryService.getAll();
      setTaskEntries(fetchedEntries);
      setError(null);
    } catch (err) {
      setError('Failed to fetch task entries');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (entry: TaskEntry | null = null) => {
    if (entry) {
      // Employees can only edit their own entries
      if (currentUserRole === 'Employee' && entry.user_id !== currentUser?.id) {
        setSnackbar({ open: true, message: 'You can only edit your own task entries', severity: 'error' });
        return;
      }
      // Managers can only edit entries in their department
      if (currentUserRole === 'Manager' && entry.department !== currentUserDepartment) {
        setSnackbar({ open: true, message: 'You can only edit task entries in your department', severity: 'error' });
        return;
      }
    }
    setCurrentTaskEntry(entry);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentTaskEntry(null);
  };

  const handleSave = async () => {
    if (!currentTaskEntry || !currentUser) return;

    try {
      let savedEntry: TaskEntry;
      
      if (currentTaskEntry.id) {
        // Update existing entry
        // Employees can only update their own entries
        if (currentUserRole === 'Employee' && currentTaskEntry.user_id !== currentUser.id) {
          setSnackbar({ open: true, message: 'You can only update your own task entries', severity: 'error' });
          return;
        }
        // Managers can only update entries in their department
        if (currentUserRole === 'Manager' && currentTaskEntry.department !== currentUserDepartment) {
          setSnackbar({ open: true, message: 'You can only update task entries in your department', severity: 'error' });
          return;
        }
        
        savedEntry = await taskEntryService.update(currentTaskEntry.id, {
          ...currentTaskEntry,
          user_id: currentUserRole === 'Employee' ? currentUser.id : currentTaskEntry.user_id,
          department: currentUserRole === 'Employee' ? currentUserDepartment : currentTaskEntry.department
        });
        setTaskEntries(taskEntries.map(e => e.id === savedEntry.id ? savedEntry : e));
        setSnackbar({ open: true, message: 'Task entry updated successfully', severity: 'success' });
      } else {
        // Create new entry
        savedEntry = await taskEntryService.create({
          ...currentTaskEntry,
          user_id: currentUserRole === 'Employee' ? currentUser.id : currentTaskEntry.user_id,
          department: currentUserRole === 'Employee' ? currentUserDepartment : currentTaskEntry.department
        });
        setTaskEntries([...taskEntries, savedEntry]);
        setSnackbar({ open: true, message: 'Task entry created successfully', severity: 'success' });
      }
      handleClose();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to save task entry', severity: 'error' });
    }
  };

  const handleDelete = async (id: number) => {
    // Employees can only delete their own entries
    const entry = taskEntries.find(e => e.id === id);
    if (currentUserRole === 'Employee' && entry?.user_id !== currentUser?.id) {
      setSnackbar({ open: true, message: 'You can only delete your own task entries', severity: 'error' });
      return;
    }
    // Managers can only delete entries in their department
    if (currentUserRole === 'Manager' && entry?.department !== currentUserDepartment) {
      setSnackbar({ open: true, message: 'You can only delete task entries in your department', severity: 'error' });
      return;
    }

    try {
      await taskEntryService.delete(id);
      setTaskEntries(taskEntries.filter(e => e.id !== id));
      setSnackbar({ open: true, message: 'Task entry deleted successfully', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete task entry', severity: 'error' });
    }
  };

  const handleChange = (field: keyof TaskEntry, value: string | number | null) => {
    if (currentTaskEntry) {
      setCurrentTaskEntry({ ...currentTaskEntry, [field]: value });
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Typography color="error">{error}</Typography>
      </div>
    );
  }

  return (
    <div>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Task Entry
        </Typography>
        <Button variant="contained" color="primary" onClick={() => handleOpen()}>
          Add Task Entry
        </Button>
      </Box>

      <Card>
        <CardContent>
          <Table>
            <TableHead>
              <TableRow>
                {currentUserRole !== 'Employee' && <TableCell>User ID</TableCell>}
                {currentUserRole === 'Admin' && <TableCell>Department</TableCell>}
                <TableCell>Task Type</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Custom Task</TableCell>
                <TableCell>Time Spent (min)</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {taskEntries.map((entry) => (
                <TableRow key={entry.id}>
                  {currentUserRole !== 'Employee' && <TableCell>{entry.user_id}</TableCell>}
                  {currentUserRole === 'Admin' && <TableCell>{entry.department}</TableCell>}
                  <TableCell>{entry.task_type}</TableCell>
                  <TableCell>{entry.quantity}</TableCell>
                  <TableCell>{entry.custom_task || '-'}</TableCell>
                  <TableCell>{entry.time_spent}</TableCell>
                  <TableCell>
                    <Button onClick={() => handleOpen(entry)} size="small">Edit</Button>
                    <Button onClick={() => handleDelete(entry.id)} color="error" size="small">Delete</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{currentTaskEntry?.id ? 'Edit Task Entry' : 'Add Task Entry'}</DialogTitle>
        <DialogContent>
          {currentUserRole !== 'Employee' && (
            <TextField
              margin="dense"
              label="User ID"
              fullWidth
              type="number"
              value={currentTaskEntry?.user_id || currentUser?.id || ''}
              onChange={(e) => handleChange('user_id', parseInt(e.target.value))}
            />
          )}
          {currentUserRole === 'Admin' && (
            <TextField
              margin="dense"
              label="Department"
              fullWidth
              value={currentTaskEntry?.department || currentUserDepartment || ''}
              onChange={(e) => handleChange('department', e.target.value)}
            />
          )}
          <FormControl fullWidth margin="dense">
            <InputLabel>Task Type</InputLabel>
            <Select
              value={currentTaskEntry?.task_type || ''}
              onChange={(e) => handleChange('task_type', e.target.value)}
            >
              <MenuItem value="Data Entry">Data Entry</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Quantity"
            fullWidth
            type="number"
            value={currentTaskEntry?.quantity || ''}
            onChange={(e) => handleChange('quantity', parseInt(e.target.value))}
          />
          <TextField
            margin="dense"
            label="Custom Task"
            fullWidth
            value={currentTaskEntry?.custom_task || ''}
            onChange={(e) => handleChange('custom_task', e.target.value)}
          />
          <TextField
            margin="dense"
            label="Time Spent (minutes)"
            fullWidth
            type="number"
            value={currentTaskEntry?.time_spent || ''}
            onChange={(e) => handleChange('time_spent', parseInt(e.target.value))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {currentTaskEntry?.id ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default TaskEntryComponent;